import { Controller } from '@hotwired/stimulus'
import { useClickOutside } from 'stimulus-use'
import { get } from '@rails/request.js'
import hotkeys from 'hotkeys-js'

export default class extends Controller {
  static targets = [
    'filterPlaceholder',
    'listbox',
    'listboxContainer',
    'loadingContainer',
    'option',
    'optionCheck',
    'resultsContainer',
    'searchInput',
    'toggle',
  ]

  static values = {
    disabled: { default: true, type: Boolean },
    drawerId: String,
    id: String,
    url: String,
  }

  connect() {
    this.hotkeysScope = 'global-folders-combobox'

    useClickOutside(this)

    hotkeys(
      'escape',
      { scope: this.hotkeysScope },
      () => { this.hide() },
    )

    hotkeys.filter = (event) => {
      const { tagName } = event.target

      return (event.target.classList.contains('js-hotkeys-enabled')) ||
        !(/^(INPUT|TEXTAREA|SELECT)/.test(tagName))
    }
  }

  clickOutside() {
    this.hide()
  }

  delayedFilter() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => { this.#filter() }, 250)
  }

  disabledValueChanged() {
    this.searchInputTarget.disabled = this.disabledValue
    this.toggleTarget.disabled = this.disabledValue
  }

  drawerIdValueChanged() {
    this.disabledValue = (this.drawerIdValue === '')
    this.#storeSelectedOption({ label: '', value: '' })
    this.dispatch('folderIdChanged', { detail: { folderId: '' } })
  }

  handleDrawerChange({ detail: { drawerId } }) {
    this.drawerIdValue = drawerId
  }

  handleUploadStateChange({ detail: { disableInput } }) {
    this.disabledValue = disableInput
  }

  hide() {
    if (hotkeys.getScope() === this.hotkeysScope) {
      hotkeys.setScope(this.previousScope)
    }

    this.#resetInputValueIfPresent()

    this.searchInputTarget.setAttribute('aria-expanded', false)
    this.resultsContainerTarget.classList.add('hidden')
  }

  initialFilter() {
    if (!this.disabledValue) {
      if (hotkeys.getScope() !== this.hotkeysScope) {
        this.previousScope = hotkeys.getScope()
      }
      hotkeys.setScope(this.hotkeysScope)

      this.resultsContainerTarget.classList.remove('hidden')
      this.#filter()
    }
  }

  select(event) {
    this.#applySelectedOptionStyling(event.target)
    this.#storeSelectedOption({
      label: event.target.dataset.searchContext,
      value: event.target.dataset.selectValue,
    })
    this.dispatch(
      'folderIdChanged',
      { detail: { folderId: event.target.dataset.selectValue } },
    )

    this.hide()
  }

  toggle() {
    if (this.resultsContainerTarget.classList.contains('hidden')) {
      if (!this.disabledValue) this.#focusInputTarget()
    } else {
      this.hide()
    }
  }

  #applySelectedOptionStyling(option) {
    const optionCheck = option.querySelector(
      "[data-clients--global-folder-combobox-target='optionCheck']",
    )

    option.classList.add('font-semibold')
    optionCheck.classList.remove('hidden')
  }

  async #filter() {
    let response

    if (this.abortController) {
      this.abortController.abort('processing new filter request')
    }

    if (this.searchInputTarget.value === '') {
      this.#showFilterPlaceholder()
    } else {
      this.abortController = new AbortController()
      this.#showLoadingContainer()

      try {
        response = await get(
          this.urlValue,
          {
            query: {
              drawer_id: this.drawerIdValue,
              term: this.searchInputTarget.value,
            },
            responseKind: 'json',
            signal: this.abortController.signal,
          },
        )
      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }

        throw error
      }

      if (!response.ok) {
        throw new Error(response.status)
      }

      const data = await response.json
      this.listboxContainerTarget.innerHTML = data.results
      this.#showListboxContainer()
      this.#styleSelectedOption()
    }
  }

  #focusInputTarget() {
    this.searchInputTarget.focus()
    const resetValueToMoveCursor = this.searchInputTarget.value
    this.searchInputTarget.value = ''
    this.searchInputTarget.value = resetValueToMoveCursor
  }

  #resetInputValueIfPresent() {
    if (this.searchInputTarget.dataset.selectedValue) {
      this.searchInputTarget.value = this.searchInputTarget.dataset.selectedLabel
    } else {
      this.searchInputTarget.value = ''
    }
  }

  #showFilterPlaceholder() {
    this.resultsContainerTarget.classList.remove('hidden')
    this.listboxContainerTarget.classList.add('hidden')
    this.loadingContainerTarget.classList.add('hidden')
    this.filterPlaceholderTarget.classList.remove('hidden')

    this.searchInputTarget.removeAttribute('aria-controls')
    this.searchInputTarget.setAttribute('aria-expanded', true)
  }

  #showListboxContainer() {
    this.resultsContainerTarget.classList.remove('hidden')
    this.loadingContainerTarget.classList.add('hidden')
    this.filterPlaceholderTarget.classList.add('hidden')
    this.listboxContainerTarget.classList.remove('hidden')
    this.listboxTarget.id = `${this.idValue}-listbox`
    this.searchInputTarget.setAttribute('aria-expanded', true)
    this.searchInputTarget.setAttribute(
      'aria-controls',
      `${this.idValue}-listbox`,
    )
  }

  #showLoadingContainer() {
    this.resultsContainerTarget.classList.remove('hidden')
    this.listboxContainerTarget.classList.add('hidden')
    this.filterPlaceholderTarget.classList.add('hidden')
    this.loadingContainerTarget.classList.remove('hidden')
    this.searchInputTarget.removeAttribute('aria-controls')
    this.searchInputTarget.setAttribute('aria-expanded', false)
  }

  #storeSelectedOption({ label, value }) {
    this.searchInputTarget.value = label
    this.searchInputTarget.dataset.selectedLabel = label
    this.searchInputTarget.dataset.selectedValue = value
  }

  #styleSelectedOption() {
    const { selectedValue } = this.searchInputTarget.dataset

    if (selectedValue) {
      const selectedOption = this.optionTargets.find(
        (el) => el.dataset.selectValue === selectedValue,
      )

      if (selectedOption) {
        this.#applySelectedOptionStyling(selectedOption)
      }
    }
  }
}
